import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { Row, Tab, Tabs, OverlayTrigger, Tooltip } from "react-bootstrap";
import ApprovalSettingsModal from "../ApproverSettingModal/ApproverSettingModal";
import CustomButton from "../../reusable_components/custom_button_component/Button";
import ClientEditor from "../ClientEditor/ClientEditor";
import ClientCreator from "../ClientCreator/ClientCreator";
import {revertApprovalStatus} from "./ClientsGrid.service";
import { ColDef, FilterChangedEvent } from "ag-grid-community";
import EditFormButtonRenderer from "./components/EditFormButtonRenderer/EditFormButtonRenderer";
import ApprovalStatusRenderer from "./components/ApprovalStatusRenderer/ApprovalStatusRenderer";
import {
  FirstPageCacheEntry,
  GridColumnTitleRenderer,
  PurposeOption,
  TimeRenderer,
  useDebounce,
} from "./ClientsGrid.utils";
import { createInCCH, fetchFirmClients, sendForApproval, deleteProspectClients, validateDeleteProspectClients, fetchOptions, } from "./ClientsGrid.service";
import ActionsRenderer from "./components/ActionsRenderer/ActionsRenderer";
import ClientFilesUploader from "../ClientFilesUploader/ClientFilesUploader";
import CygnusGrid from "../../../pages/reusable_components/CygnusGrid/CygnusGrid";
import RefreshGridButton from "../../../pages/reusable_components/RefreshGridButton/RefreshGridButton";
import styles from "./ClientsGrid.module.css";
import {
  CygnusGridRef,
  dateFilterParams,
  selectedFirstValuesProvider,
  setFilterValues,
} from "../../reusable_components/CygnusGrid/CygnusGrid.utils";
import CreateEngagementLetterWizard from "../../engagement_letter/CreateEngagementLetterWizard/CreateEngagementLetterWizard";
import { Dropdown } from "react-bootstrap";
import ClientActionsRenderer from "./components/ClientActionsRenderer/ClientActionsRenderer";
import ClientGroupManagerModal from "./components/ClientGroupManagerModal/ClientGroupManagerModal";
import { fetchClientGroups } from "./ClientsGrid.service";
import ExportClientsToExcelModal from "./components/ExportClientsToExcelModal/ExportClientsToExcelModal";
import { TemplateOption } from "../../../pages/engagement_letter/CreateEngagementLetterWizard/components/EngagementLetterTemplateSelector/EngagementLetterTemplateSelector.utils";
import AuditTrailModal from "../../reusable_components/AuditTrailModal/AuditTrailModal";
import NextActionRenderer from "./components/NextActionRenderer/NextActionRenderer";
import { useGlobalStore } from "../../store/globalStore";
import {
  createPartnerProvider,
  createPurposeProvider,
  createUsersProvider,
  createClientTypesProvider,
  createStatusProvider,
  createLabelMap,
} from "../../../pages/reusable_components/CygnusGrid/CygnusGrid.utils";
import ViewToggle, {
  ViewMode,
} from "../../../Reusables/components/ViewToggle/ViewToggle";
import {
  buildServerFilterPayload,
  applyGridModelToUI,
  mergeServerFilters,
  normalizeServerFilters,
} from "../../reusable_components/CygnusGrid/CygnusGrid.utils";
import {
  saveGridFilters,
  fetchGridFilters,
} from "../../reusable_components/CygnusGrid/CygnusGrid.service";
import { AlertModal } from "../../../Reusables/components/AlertModal/AlertModal";
import { initializeClientGlobalOptions } from "../../utils/globalInitializer";
import ApprovalPdfViewerModal from "./components/ApprovalPdfViewerModal/ApprovalPdfViewerModal";
import UploadSpreadsheetModal from "../UploadSpreadsheetModal/UploadSpreadsheetModal";
import { BackendOption } from "../UploadSpreadsheetModal/UploadSpreadsheetModal.types";
import { hasFeature } from "../../../utils";
import { getFromLS } from "../../../utils/storage.js";
import { FunnelFill } from "react-bootstrap-icons";
import CreatedDateFloatingFilter from "./components/CreatedDateFloatingFilter/CreatedDateFloatingFilter";
import { filter } from "lodash";

/**
 * ClientsGrid
 *
 * Main component for managing clients and prospects with rich grid features:
 * - Client creation, editing, and approval workflows
 * - Document uploads and background check tracking
 * - MyView/AllView toggle with persistent filter preferences
 * - Dual-tab interface (Prospects vs Clients in Practice)
 * - AG Grid with pagination, filtering, and custom cell renderers
 * - Integration with file uploader and engagement letter wizard
 * 
 * Filter Persistence Strategy:
 * - MyView: Filters are saved to backend and automatically restored on page load
 * - AllView: Filters are temporary and never persisted
 * - Each tab (Prospects/Clients) has its own saved filter state
 * - Toggle between views maintains appropriate filter state for each mode
 */
const ClientsGrid: React.FC<{
    transporter: any;
    saveParamToApi?: (parameter_name: string, value: any) => Promise<void>;
  }> = ({ transporter, saveParamToApi }) => {
  // ==================== Modal & UI State ====================
  const [clientEditorShow, setClientEditorShow] = useState<boolean>(false);
  const [prospectEditorShow, setProspectEditorShow] = useState<boolean>(false);
  const [clientCreatorShow, setClientCreatorShow] = useState<boolean>(false);
  const [addNewWizardShow, setAddNewWizardShow] = useState<boolean>(false);
  const [uploaderShow, setUploaderShow] = useState<boolean>(false);
  const [approvalSettingModal, setApprovalSettingModal] = useState<boolean>(false);
  const [showGroupModal, setShowGroupModal] = useState<boolean>(false);
  const [downloadExcelDataSpreadSheet, setDownloadExcelDataSpreadSheetShow] = useState<boolean>(false);
  const [auditTrailModalShow, setAuditTrailModalShow] = useState(false);
  const [showClearConfirm, setShowClearConfirm] = useState(false);

  // ==================== Client Data State ====================
  const [clientMasterId, setClientMasterId] = useState<number>(0);
  const [formPurpose, setFormPurpose] = useState<string>("");
  const [clientName, setClientName] = useState<string>("");
  const [isClientPublic, setIsClientPublic] = useState<boolean>(false);
  const [clientRowData, setClientRowData] = useState<any>(0);
  const [clientTypeCode, setClientTypeCode] = useState<number | null>(null);
  const [clientId, setClientId] = useState<string>();
  const [clientPurpose, setClientPurpose] = useState<PurposeOption | null>(null);
  const [clientDetailsPdfUrl, setClientDetailsPdfUrl] = useState<string>("");

  // ==================== Action State ====================
  const [sendForApprovalBulkApiProgress, setSendForApprovalBulkApiProgress] = useState<boolean>(false);
  const [revertApprovalApiProgress, setRevertApprovalApiProgress] = useState<boolean>(false);
  const [createInCCHApiProgress, setCreateInCCHApiProgress] = useState<boolean>(false);
  const [addNewClientApiProgress, _setAddNewClientApiProgress] = useState<boolean>(false);
  const [refreshApiProgress, setRefreshApiProgress] = useState<boolean>(false);
  const [searchText, setSearchText] = useState<string>();
  const [purposeLoading, setPurposeLoading] = useState(false);
  const [purposeError, setPurposeError] = useState<string | null>(null);
  const [initTriggered, setInitTriggered] = useState(false);
  const [uploadSpreadsheetModal, setUploadSpreadsheetModalShow] = useState<boolean>(false);
  const [showActionAlert, setShowActionAlert] = useState(false);
  const [actionAlertTitle, setActionAlertTitle] = useState("");
  const [actionAlertMessage, setActionAlertMessage] = useState("");
  const [createInCCHDisabledReason, setCreateInCCHDisabledReason] = useState<string | null>(null);
  const [sendForApprovalDisabledReason, setSendForApprovalDisabledReason] = useState<string | null>(null);
  const [revertApprovalDisabledReason, setRevertApprovalDisabledReason] = useState<string | null>(null)
  const [allApprove, setAllApprove] = useState<boolean>(false);

  // ==================== Delete State ====================
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteInProgress, setDeleteInProgress] = useState(false);
  const [deleteCount, setDeleteCount] = useState(0);
  const [deleteWarningCount, setDeleteWarningCount] = useState(0);
  const [deleteMessage, setDeleteMessage] = useState("");
  const [deleteWarningSignedClientIds, setDeleteWarningSignedClientIds] = useState<string[]>([]);
  const [deleteValidationDone, setDeleteValidationDone] = useState(false);
  const [deleteValidationLoading, setDeleteValidationLoading] = useState(false);
  const [prospectDeleteReasonOptions, setProspectDeleteReasonOptions] = useState<any[]>([]);
  const [selectedDeleteReason, setSelectedDeleteReason] = useState<string>("");
  const [deleteComment, setDeleteComment] = useState<string>("");
  
  // ==================== Revert Approval State ====================
  const [showRevertApprovalConfirm, setShowRevertApprovalConfirm] = useState(false);
  const [revertApprovalMessage, setRevertApprovalMessage] = useState("");
  const [revertApprovalCount, setRevertApprovalCount] = useState(0);
  const [revertApprovalClientIds, setRevertApprovalClientIds] =useState<string[]>([]);
  const [revertComment, setRevertComment] = useState("");
  const [prospectRevertReasonOptions, setProspectRevertReasonOptions] = useState<any[]>([]);
  const [selectedRevertReason, setSelectedRevertReason] = useState<string>("");

  const availableFeatures = getFromLS("active-features");

  const featureSet = new Set(
    Array.isArray(availableFeatures) ? availableFeatures : []
  );
  const isFirmAdmin =
    featureSet.has("FRMADMN");
  const status = String(clientRowData?.ApprovalStatus ?? "")
  const isApproved =
    status === "approved" || status === "partially approved";
 
  const disableApprovedProspect = isApproved && !isFirmAdmin;
  const showBulkCreate = featureSet.has("CLIENT_BULK_CREATE");
  const showCreateInCCH = isFirmAdmin;
  const canDeleteProspect = featureSet.has("CLIENT_DELETE");

  // ==================== View Mode & Tab State ====================
  /**
   * View Mode Toggle:
   * - "myView": Filters are saved and restored from backend
   * - "allView": Filters are temporary and not persisted
   */
  const [viewMode, setViewMode] = useState<ViewMode>("myView");

  /**
   * Active Tab:
   * - "prospect": Prospects tab (clients pending approval)
   * - "clients": Clients in Practice tab (approved clients)
   * Each tab has its own saved filter state
   */
  type EventKey = string | number;
  const [activeTab, setActiveTab] = useState<EventKey>(() => {
    const params = new URLSearchParams(window.location.search);
    const targetTabFromUrl = params.get("targetTab");

    if (targetTabFromUrl === "prospect") return "prospect";
    if (targetTabFromUrl === "clients") return "clients";

    const targetTab = localStorage.getItem("clients_dashboard_target_tab");
    return targetTab === "prospect" ? "prospect" : "clients";
  });

  const activeTabRef = useRef<EventKey>(activeTab);

  useEffect(() => {
    activeTabRef.current = activeTab;
  }, [activeTab]);

  // ==================== Grid State ====================
  const [prospectColumnDefs, setProspectColumnDefs] = useState<any>([]);
  const [clientColumnDefs, setClientColumnDefs] = useState<any>([]);
  const [clientGroups, setClientGroups] = useState<any>([]);
  const [selectedRowsProspects, setSelectedRowsProspects] = useState<any>([]);
  const [selectedRowsClients, setSelectedRowsClients] = useState<any>([]);
  const [engagementLetterTemplateOptions, setEngagementLetterTemplateOptions] = useState<TemplateOption[]>([]);

  // ==================== Filter State ====================
  /**
   * Saved filter state from backend (only used in MyView)
   * Contains: { gridModel: AGGrid filter model, serverFilter: backend filter params }
   * Separate state per tab (prospects vs clients)
   */
  const [savedByTab, setSavedByTab] = useState<Record<string, any>>({prospect: null,clients: null,});
  const savedByTabRef = useRef<Record<string, any>>({prospect: null,clients: null,});
  const [savedLoadedByTab, setSavedLoadedByTab] = useState<Record<string, boolean>>({prospect: false,clients: false,});
  const saved = savedByTab[String(activeTab)];
  const savedLoaded = savedLoadedByTab[String(activeTab)];
  const getActiveSaved = () => {
    return savedByTabRef.current[String(activeTab)] || null;
  };

  const [approvalPdfViewerShow, setApprovalPdfViewerShow] = useState<boolean>(false);

  /**
   * viewModeRef: Tracks current viewMode to avoid closure issues in event listeners
   * This ensures the filter change listener always checks the CURRENT viewMode,
   * not the value captured when the listener was created
   */
  const viewModeRef = useRef(viewMode);

  /**
   * suppressFilterEventsRef: Prevents filter change events from triggering saves
   * during programmatic filter updates (e.g., when toggling views or tabs)
   */
  const suppressFilterEventsRef = useRef(false);

  /**
   * debouncedTimerRef: Stores timeout ID for debounced filter saves
   * Allows cancellation of pending saves when needed
   */
  const debouncedTimerRef = useRef<number | null>(null);

  /**
   * filterListenerRef: Stores reference to the AG Grid filter change listener
   * Allows removal/re-attachment to prevent false triggers during programmatic changes
   */
  const filterListenerRef = useRef<Record<string, ((event: FilterChangedEvent) => void) | null>>({prospect: null,clients: null,});

  const [hasActiveFilters, setHasActiveFilters] = useState(false);
  const [activeFilterCount, setActiveFilterCount] = useState(0);
  const [filterIndicatorReady, setFilterIndicatorReady] = useState(false);

  // ==================== Grid References ====================
  const prospectGridRef = useRef<CygnusGridRef>(null);
  const clientGridRef = useRef<CygnusGridRef>(null);

  // Document validation warning state
  const [showDocumentWarning, setShowDocumentWarning] = useState(false);
  const [documentWarningTitle, setDocumentWarningTitle] = useState("");
  const [documentWarningMessage, setDocumentWarningMessage] = useState("");
  const [documentWarningList, setDocumentWarningList] = useState<string[]>([]);

  /**
   * Returns the API for the currently active grid (prospects or clients)
   */
  const getActiveGridApi = () =>
    (activeTab === "prospect" ? prospectGridRef.current : clientGridRef.current)?.api;
  const kpiPayloadHandledRef = useRef(false);

  // ==================== Grid Configuration ====================
  /**
   * Grid key changes based on active tab
   * Used for saving/loading filter state per tab
   */
  const gridKey = useMemo(() => {
    return activeTab === "prospect"
      ? "ClientsGrid.prospects"
      : "ClientsGrid.clients";
  }, [activeTab]);

  const getKpiPayloadFromUrl = () => {
    const params = new URLSearchParams(window.location.search);
    const encodedPayload = params.get("kpiPayload");

    if (!encodedPayload) return null;

    try {
      return JSON.parse(atob(decodeURIComponent(encodedPayload)));
    } catch (e) {
      return null;
    }
  };

  const clearKpiPayloadFromUrl = () => {
    const params = new URLSearchParams(window.location.search);
    params.delete("kpiPayload");
    params.delete("targetTab");

    const nextUrl = `${window.location.pathname}${
      params.toString() ? `?${params.toString()}` : ""
    }`;

    window.history.replaceState({}, "", nextUrl);
  };

  const saveDashboardPayloadFilters = (payload: any) => {
    if (!payload || viewModeRef.current !== "myView" || !userId) return;

    window.setTimeout(() => {
      saveGridFilters({
        gridKey: payload.gridKey || gridKey,
        userId: String(userId),
        savedAt: new Date().toISOString(),
        viewMode: "myView",
        gridModel: payload.gridModel || {},
        serverFilter: payload.serverFilter || {},
      }).catch((err) => {
        console.error("Failed to save dashboard KPI filters:", err);
      });
    }, 500);
  };

  const userId = localStorage.getItem("userId");

  /**
   * Filter key mapping: AG Grid column IDs → Backend API field names
   * Used to translate grid filters to server-side query parameters
   */
  const prospectFilterKeyMap = {
    clientId: "ClientId",
    clientName: "ClientName",
    clientTypeName: "ClientTypeName",
    officeLocation: "OfficeLocation",
    lineOfService: "LineOfService",
    primaryPartner: "PrimaryPartner",
    originatingPartner: "OriginatingPartner",
    clientGroupCode: "Group",
    approvalStatus: "Status",
    backgroundCheckStatus: "BackgroundCheckStatus",
    active: "Active",
    createdBy: "CreatedBy",
    lastUpdatedDate: "LastUpdatedDate",
    createdDate: "CreatedDate",
  };

  const clientFilterKeyMap = {
    clientId: "ClientId",
    clientName: "ClientName",
    clientTypeName: "ClientTypeName",
    officeLocation: "OfficeLocation",
    lineOfService: "LineOfService",
    primaryPartner: "PrimaryPartner",
    originatingPartner: "OriginatingPartner",
    clientGroupCode: "Group",
    approvalStatus: "Status",
    backgroundCheckStatus: "BackgroundCheckStatus",
    active: "Active",
    createdBy: "CreatedBy",
    lastUpdatedDate: "LastUpdatedDate",
    createdDate: "CreatedDate",
  };

  // ==================== Global Store Options ====================
  const officeLocationOptions = useGlobalStore((s) => s.officeLocationOptions);
  const purposeOptions = useGlobalStore((s) => s.purposeOptions);
  const primaryPartnerOptions = useGlobalStore((s) => s.primaryPartnerOptions);
  const users = useGlobalStore((s) => s.users);
  const clientTypes = useGlobalStore((s) => s.clientTypes);
  const clientStatus = useGlobalStore((s) => s.clientStatus);
  const version = useGlobalStore((s) => s.version);
  const hydrated = useGlobalStore((s) => s.hydrated);

  // ==================== Initialize Global Options ====================
  /**
   * Ensures global dropdown options are loaded on first render
   * Only triggers once when data is missing
   */
  useEffect(() => {
    if (!hydrated) return;
    if (initTriggered) return;

    const isGlobalDataMissing =
      !purposeOptions?.length ||
      !primaryPartnerOptions?.length ||
      !users?.length ||
      !clientTypes?.length ||
      !clientStatus?.length;

    if (isGlobalDataMissing) {
      setInitTriggered(true);
      initializeClientGlobalOptions();
    }
  }, [
    hydrated,
    version,
    purposeOptions,
    primaryPartnerOptions,
    officeLocationOptions,
    users,
    clientTypes,
    clientStatus,
    initTriggered,
  ]);

  const filtersReady =
    hydrated &&
    !!officeLocationOptions?.length &&
    !!purposeOptions?.length &&
    !!primaryPartnerOptions?.length &&
    !!users?.length &&
    !!clientTypes?.length &&
    !!clientStatus?.length;

  const activeGridColumnsReady =
    activeTab === "prospect"
      ? prospectColumnDefs.length > 0
      : clientColumnDefs.length > 0;

  // ==================== Keep viewModeRef in Sync ====================
  /**
   * Updates viewModeRef whenever viewMode changes
   * This ensures event listeners always see the current viewMode
   */
  useEffect(() => {
    viewModeRef.current = viewMode;
  }, [viewMode]);

  // ==================== Load Saved Filters (MyView Only) ====================
  /**
   * CRITICAL: Only loads saved filters when in MyView
   * 
   * MyView: Fetches and applies saved filter preferences from backend
   * AllView: Clears saved state (filters are temporary)
   * 
   * This prevents AllView filters from being persisted accidentally
   * Runs whenever tab or viewMode changes
   */
  useEffect(() => {
    let cancelled = false;
    const tabKey = String(activeTab);

    (async () => {
      setSavedLoadedByTab((prev) => ({
        ...prev,
        [tabKey]: false,
      }));

      setFilterIndicatorReady(false);

      let nextSaved: any = null;

      if (viewModeRef.current === "myView" && userId !== null) {
        const navPayload = !kpiPayloadHandledRef.current
          ? getKpiPayloadFromUrl()
          : null;

        if (navPayload) {
          const targetTab =
            navPayload.gridKey === "ClientsGrid.prospects" ? "prospect" : "clients";

          if (activeTab !== targetTab) {
            setActiveTab(targetTab);
            return;
          }

          kpiPayloadHandledRef.current = true;
          clearKpiPayloadFromUrl();

          nextSaved = navPayload;
          saveDashboardPayloadFilters(navPayload);
        } else {
          nextSaved = await fetchGridFilters(userId, gridKey);
        }
      }

      if (cancelled) return;

      setSavedByTab((prev) => ({
        ...prev,
        [tabKey]: nextSaved || null,
      }));

      savedByTabRef.current = {
        ...savedByTabRef.current,
        [tabKey]: nextSaved || null,
      };

      setSavedLoadedByTab((prev) => ({
        ...prev,
        [tabKey]: true,
      }));
    })();

    return () => {
      cancelled = true;
    };
  }, [activeTab, viewMode, userId, gridKey]);


  // ==================== Client Prospect Delete ===================
  const deleteDisabled =
    activeTab !== "prospect" || selectedRowsProspects.length === 0;

  const deleteTooltipMessage =
    activeTab !== "prospect"
      ? "Delete is available only for prospects."
      : selectedRowsProspects.length === 0
        ? "Please select one or more prospects to delete."
        : "";

  const handleOpenDeleteConfirm = async () => {
    const ids = (selectedRowsProspects || [])
      .map((row: any) => Number(row.ClientMasterId))
      .filter((id: number) => !Number.isNaN(id));

    if (!ids.length) return;

    setDeleteCount(ids.length);
    setDeleteWarningCount(0);
    setDeleteMessage("");
    setDeleteWarningSignedClientIds([]);
    setSelectedDeleteReason("");
    setDeleteComment("");
    setDeleteValidationDone(false);
    setDeleteValidationLoading(true);
    setShowDeleteConfirm(true);

    try {
      const result = await validateDeleteProspectClients(ids, "");

      setDeleteWarningCount(result.warning_count || 0);
      setDeleteMessage(result.message || "");
      setDeleteWarningSignedClientIds(result.warning_client_ids || []);
      setDeleteValidationDone(true);
    } finally {
      setDeleteValidationLoading(false);
    }
  };

  const handleConfirmDeleteProspects = async () => {
    const ids = (selectedRowsProspects || [])
      .map((row: any) => Number(row.ClientMasterId))
      .filter((id: number) => !Number.isNaN(id));

    if (!ids.length) {
      setShowDeleteConfirm(false);
      return;
    }

    const combinedDeletionReason = deleteComment.trim()
      ? `${selectedDeleteReason}: ${deleteComment.trim()}`
      : selectedDeleteReason;

    try {
      setDeleteInProgress(true);

      const result = await deleteProspectClients(ids, combinedDeletionReason);

      if (result.success) {
        setSelectedRowsProspects([]);
        deselectRows();
        setSelectedDeleteReason("");
        setDeleteComment("");
        setDeleteValidationDone(false);
        setDeleteValidationLoading(false);
        setShowDeleteConfirm(false);
        handleRefresh();
      }
    } catch (err) {
      console.error("Prospect delete error:", err);
    } finally {
      setDeleteInProgress(false);
    }
  };

  useEffect(() => {
    const fetchProspectDeleteReasons = async () => {
      const options = await fetchOptions("get_prospect_delete_reason", false);
      setProspectDeleteReasonOptions(options || []);
    };
    const fetchProspectRevertReasons = async () => {
      const options = await fetchOptions("get_prospect_revert_reason", false);
      setProspectRevertReasonOptions(options || []);
    };
    fetchProspectRevertReasons();
    fetchProspectDeleteReasons();
  }, []);

  // ==================== Debounced Search ====================
  const debouncedSearchText = useDebounce(searchText, 300);

  // ==================== MyView Filter Lists ====================
  /**
   * Partners to filter by in MyView (based on user's checked preferences)
   * These are automatically applied when in MyView unless saved filters override them
   */
  const partnersForMyView: string[] = useMemo(() => {
    if (!Array.isArray(primaryPartnerOptions)) return [];
    return primaryPartnerOptions
      .filter((p: any) => p?.checked === true)
      .map((p: any) => String(p?.value))
      .filter(Boolean);
  }, [primaryPartnerOptions]);

  const allSetFilterValuesByKey = useMemo(() => {
    const uniqueValues = (items: any[], getValue: (item: any) => unknown) =>
      Array.from(
        new Set(
          (Array.isArray(items) ? items : [])
            .map((item) => String(getValue(item) ?? "").trim())
            .filter(Boolean)
        )
      );
    const partnerValues = uniqueValues(primaryPartnerOptions, (item) => item?.value);

    return {
      ClientTypeName: uniqueValues(clientTypes, (item) => item?.ReferenceDataValue),
      OfficeLocation: uniqueValues(officeLocationOptions, (item) => item?.value),
      LineOfService: uniqueValues(purposeOptions, (item) => item?.value ?? item?.code),
      PrimaryPartner: partnerValues,
      OriginatingPartner: partnerValues,
      Status: uniqueValues(clientStatus, (item) => item?.ReferenceDataValue),
      CreatedBy: uniqueValues(users, (item) => item?.UserFullName),
    };
  }, [clientTypes, officeLocationOptions, purposeOptions, primaryPartnerOptions, clientStatus, users]);

  const allSetFilterValuesByKeyRef = useRef(allSetFilterValuesByKey);
  allSetFilterValuesByKeyRef.current = allSetFilterValuesByKey;

  /**
   * Office locations to filter by in MyView (based on user's checked preferences)
   */
  const officeLocationsForMyView: string[] = useMemo(() => {
    if (!Array.isArray(officeLocationOptions)) return [];
    return officeLocationOptions
      .filter((loc: any) => loc?.checked === true)
      .map((loc: any) => String(loc?.value))
      .filter(Boolean);
  }, [officeLocationOptions]);

  // ==================== MyView Filter Application Logic ====================
  const syncFilterIndicatorState = useCallback(
  (apiParam?: any, tabKeyParam?: "prospect" | "clients") => {
    const tabKey = tabKeyParam || (String(activeTab) as "prospect" | "clients");

    // Do not let hidden/inactive tab update the visible badge count
    if (String(activeTabRef.current) !== tabKey) return;

    const api =
      apiParam ||
      (tabKey === "prospect"
        ? prospectGridRef.current?.api
        : clientGridRef.current?.api);

    const activeSaved = savedByTabRef.current[tabKey] || null;
    const gridModel = api?.getFilterModel?.() || activeSaved?.gridModel || {};

    const filterCount = Object.keys(gridModel || {}).filter(
      (key) => gridModel[key] !== null && gridModel[key] !== undefined
    ).length;

    const hasGridFilters = filterCount > 0;

    const hasMyViewDefaults =
      viewModeRef.current === "myView" &&
      !activeSaved?.gridModel &&
      ((partnersForMyView?.length ?? 0) > 0 ||
        (officeLocationsForMyView?.length ?? 0) > 0);

    const myViewDefaultCount =
      !activeSaved?.gridModel
        ? Number((partnersForMyView?.length ?? 0) > 0) +
          Number((officeLocationsForMyView?.length ?? 0) > 0)
        : 0;

    const hasFilters = hasGridFilters || hasMyViewDefaults;

    setHasActiveFilters(hasFilters);
    setActiveFilterCount(
      hasFilters ? Math.max(filterCount, myViewDefaultCount) : 0
    );
    setFilterIndicatorReady(true);
  },
  [partnersForMyView, officeLocationsForMyView, activeTab]
);

  // ==================== First Page Caching ====================
  /**
   * Caches the first page of results for instant tab/view switching
   * Cache is invalidated after 10 minutes or when data is refreshed
   */
  const firstPageCacheRef = useRef<Record<string, FirstPageCacheEntry>>({});
  const CACHE_TTL_MS = 10 * 60 * 1000; // 10 minutes

  const partnersKey = (mode: ViewMode) =>
    mode === "myView" ? partnersForMyView.join(",") : "none";

  const myViewKey = (mode: ViewMode, tabKey: EventKey) => {
    const tabSaved = savedByTabRef.current[String(tabKey)] || null;

    return mode === "myView"
      ? JSON.stringify(tabSaved?.serverFilter ?? {})
      : "none";
  };

  const stableStringify = (obj: any) =>
    JSON.stringify(obj, Object.keys(obj || {}).sort());

  const makeCacheKey = (
    tab: EventKey,
    mode: ViewMode,
    search: string | undefined,
    agFilterModel: any
  ) =>
    `${tab}|${mode}|${(search ?? "").trim()}|${partnersKey(mode)}|${myViewKey(
      mode,
      tab
    )}|${stableStringify(agFilterModel)}`;


  // Date Filter for Created Date
    const normalizeCreatedDateFilter = (
    filter: Record<string, any>,
    agFilterModel: Record<string, any>
  ) => {
    const next = { ...filter };
    const createdDateModel = agFilterModel?.createdDate;

    delete next.CreatedDate;

    if (!createdDateModel) return next;

    const filterType = createdDateModel.type;
    const fromDate = createdDateModel.dateFrom || createdDateModel.filter;
    const toDate = createdDateModel.dateTo || createdDateModel.filterTo;

    if (filterType === "equals" && fromDate) {
      next.CreatedDateFrom = fromDate;
      next.CreatedDateTo = fromDate;
    }

    if (filterType === "inRange") {
      if (fromDate) next.CreatedDateFrom = fromDate;
      if (toDate) next.CreatedDateTo = toDate;
    }

    if (filterType === "greaterThan" && fromDate) {
      next.CreatedDateFrom = fromDate;
      delete next.CreatedDateTo;
    }

    if (filterType === "lessThan" && fromDate) {
      next.CreatedDateTo = fromDate;
      delete next.CreatedDateFrom;
    }

    return next;
  };

  /**
   * Wrapped fetch function that:
   * 1. Checks cache for first page results
   * 2. Builds server filter payload from AG Grid filters
   * 3. Merges with MyView partner/office filters if applicable
   * 4. Caches successful first page responses
   */
  const wrappedFetchFirmClients = useCallback(
    async (params: any) => {
      const { offset = 0, searchText } = params;

      const requestTab: "prospect" | "clients" =
        params?.sourceCode === "CCH_AXCESS" ? "clients" : "prospect";

      const incoming = params?.filter ?? params?.filters ?? {};
      let nextFilter: Record<string, any> = { ...incoming };

      const agFilterModel = params?.filterModel ?? {};
      const hasNoAgFilters =
        !agFilterModel || Object.keys(agFilterModel).length === 0;

      const isFirstPage = offset === 0;
      const cacheKey = makeCacheKey(
        requestTab,
        viewMode,
        searchText,
        agFilterModel
      );

      // Check cache for first page with no AG filters
      if (isFirstPage && hasNoAgFilters) {
        const cached = firstPageCacheRef.current[cacheKey];
        if (cached && Date.now() - cached.savedAt < CACHE_TTL_MS) {
          return { records: cached.records, totalCount: cached.totalCount };
        }
      }

      // Build server filter from AG Grid filter model
      const keyMap =
        requestTab === "prospect" ? prospectFilterKeyMap : clientFilterKeyMap;
      const serverFilter = buildServerFilterPayload(agFilterModel, keyMap);

      // In MyView, merge AG filters with partner/office filters
      if (viewMode === "myView" && Object.keys(serverFilter).length > 0) {
        nextFilter = mergeServerFilters(nextFilter, serverFilter);
      }

      nextFilter = normalizeServerFilters(
        nextFilter,
        allSetFilterValuesByKeyRef.current
      );

      const response: any = await fetchFirmClients({
        ...params,
        filter: normalizeCreatedDateFilter(nextFilter, agFilterModel),
      });

      syncFilterIndicatorState(undefined, requestTab);

      // Cache first page results
      if (isFirstPage && hasNoAgFilters) {
        firstPageCacheRef.current[cacheKey] = {
          records: response.records,
          totalCount: response.totalCount,
          savedAt: Date.now(),
        };
      }

      return response;
    },
    [
      viewMode,
      partnersForMyView,
      officeLocationsForMyView,
      syncFilterIndicatorState,
    ]
  );

  // ==================== Filter Application ====================
  /**
   * Applies filters when switching between MyView/AllView or tabs
   * 
   * CRITICAL: Removes filter listener before changes, then re-attaches after
   * This prevents programmatic filter changes from triggering saves
   * 
   * MyView:
   * - If saved filters exist, apply them
   * - Otherwise, apply default partner/office filters
   * 
   * AllView:
   * - Clear all filters
   * - Remove partner/office filters
   */
  const applyViewModeFilters = useCallback((apiParam?: any, tabKeyParam?: "prospect" | "clients") => {
    if (!savedLoaded || !filtersReady || !activeGridColumnsReady) return;

    const tabKey = tabKeyParam || (String(activeTab) as "prospect" | "clients");

    const api =
      apiParam ||
      (tabKey === "prospect"
        ? prospectGridRef.current?.api
        : clientGridRef.current?.api);

    if (!api) return;

    if (String(activeTab) === tabKey) {
      setFilterIndicatorReady(false);
    }

    suppressFilterEventsRef.current = true;

    const currentListener = filterListenerRef.current[tabKey];
    const tabSaved = savedByTabRef.current[tabKey] || null;

    if (currentListener) {
      api.removeEventListener("filterChanged", currentListener);
    }

    if (viewMode === "myView") {
      if (tabSaved?.gridModel) {
        api.setFilterModel(null);
        applyGridModelToUI(api, tabSaved.gridModel);
      } else {
        api.setFilterModel(null);
        setFilterValues(api, "primaryPartner", partnersForMyView);
        setFilterValues(api, "officeLocation", officeLocationsForMyView);
      }
    } else {
      api.setFilterModel(null);
      setFilterValues(api, "primaryPartner", null);
      setFilterValues(api, "officeLocation", null);
    }

    setTimeout(() => {
      syncFilterIndicatorState(api, tabKey);
      suppressFilterEventsRef.current = false;

      if (currentListener) {
        api.addEventListener("filterChanged", currentListener);
      }
    }, 100);
  }, [viewMode, saved, savedLoaded, filtersReady, activeGridColumnsReady, partnersForMyView, officeLocationsForMyView, activeTab, syncFilterIndicatorState,]);

  /**
   * Apply filters when view mode, tab, or saved filters change
   */
  useEffect(() => {
    applyViewModeFilters(undefined, String(activeTab) as "prospect" | "clients");
  }, [
    viewMode,
    savedLoaded,
    filtersReady,
    activeGridColumnsReady,
    partnersForMyView,
    officeLocationsForMyView,
    activeTab,
  ]);



  // ==================== Filter Saving (MyView Only) ====================
  /**
   * Saves current filter state to backend (MyView only)
   * 
   * 1. Gets current filter model from AG Grid
   * 2. Converts to server filter format
   * 3. Saves to backend
   * 4. Updates local state
   */
  const saveMyViewFilters = useCallback(async () => {
    if (viewModeRef.current !== "myView") return;

    const tabKey = String(activeTabRef.current) as "prospect" | "clients";

    const api =
      tabKey === "prospect"
        ? prospectGridRef.current?.api
        : clientGridRef.current?.api;

    if (!api) return;

    const gridModel = api.getFilterModel();

    const keyMap =
      tabKey === "prospect" ? prospectFilterKeyMap : clientFilterKeyMap;

    const serverFilter = buildServerFilterPayload(gridModel, keyMap);

    await saveGridFilters({
      gridKey:
        tabKey === "prospect"
          ? "ClientsGrid.prospects"
          : "ClientsGrid.clients",
      userId: String(userId ?? ""),
      savedAt: new Date().toISOString(),
      viewMode: "myView",
      gridModel,
      serverFilter,
    });
  }, [userId]);

  /**
   * Debounced save: Waits 400ms after last filter change before saving
   * Prevents excessive API calls when user is actively filtering
   */
  const debouncedSave = useCallback(() => {
    if (debouncedTimerRef.current) {
      window.clearTimeout(debouncedTimerRef.current);
    }
    debouncedTimerRef.current = window.setTimeout(() => {
      if (!suppressFilterEventsRef.current) {
        saveMyViewFilters();
      }
    }, 400);
  }, [saveMyViewFilters]);

  // ==================== Filter Change Listener ====================
  /**
   * Attaches listener to AG Grid's filterChanged event
   * 
   * Only saves filters when:
   * 1. In MyView (checked via viewModeRef to avoid closure issues)
   * 2. Not suppressing events (not during programmatic changes)
   * 
   * Uses debouncedSave to batch rapid filter changes
   */
  const attachMyViewFilterListener = useCallback(
    (api: any, tabKey: "prospect" | "clients") => {
      if (!api) return;

      const existingListener = filterListenerRef.current[tabKey];

      if (existingListener) {
        api.removeEventListener("filterChanged", existingListener);
      }

      const listener = (_event: FilterChangedEvent) => {
        if (String(activeTabRef.current) !== tabKey) return;

        syncFilterIndicatorState(api, tabKey);

        if (viewModeRef.current === "myView" && !suppressFilterEventsRef.current) {
          debouncedSave();
        }
      };

      filterListenerRef.current[tabKey] = listener;
      api.addEventListener("filterChanged", listener);
    },
    [debouncedSave, syncFilterIndicatorState]
  );

  /**
   * Cleanup: Remove listener on unmount
   */
  useEffect(() => {
    return () => {
      const prospectApi = prospectGridRef.current?.api;
      const clientApi = clientGridRef.current?.api;

      const prospectListener = filterListenerRef.current.prospect;
      const clientListener = filterListenerRef.current.clients;

      if (prospectApi && prospectListener) {
        prospectApi.removeEventListener("filterChanged", prospectListener);
      }

      if (clientApi && clientListener) {
        clientApi.removeEventListener("filterChanged", clientListener);
      }
    };
  }, []);

  // ==================== Clear Filters ====================
  /**
   * Clears all filters and resets to defaults
   * 
   * MyView: Clears filters, applies default partner/office filters, saves to backend
   * AllView: Just clears filters (no save needed)
   */
  const clearAllFilters = useCallback(() => {
    const api = getActiveGridApi();
    if (!api) return;

    // Cancel any pending debounced saves
    if (debouncedTimerRef.current) {
      window.clearTimeout(debouncedTimerRef.current);
      debouncedTimerRef.current = null;
    }

    suppressFilterEventsRef.current = true;

    api.setFilterModel(null);

    if (viewMode === "myView") {
      // Apply default MyView filters
      setFilterValues(api, "primaryPartner", partnersForMyView);
      setFilterValues(api, "officeLocation", officeLocationsForMyView);

      // Save to backend
      const gridModel = api.getFilterModel();
      const keyMap =
        activeTab === "prospect" ? prospectFilterKeyMap : clientFilterKeyMap;
      const serverFilter = buildServerFilterPayload(gridModel, keyMap);

      saveGridFilters({
        gridKey,
        userId: String(userId ?? ""),
        savedAt: new Date().toISOString(),
        viewMode,
        gridModel,
        serverFilter,
      });

      const tabKey = String(activeTab);
      const payload = { gridModel, serverFilter };

      setSavedByTab((prev) => ({
        ...prev,
        [tabKey]: payload,
      }));

      savedByTabRef.current = {
        ...savedByTabRef.current,
        [tabKey]: payload,
      };
    }
    syncFilterIndicatorState(api);

    // Reset suppress flag after all filter events settle
    Promise.resolve().then(() => {
      suppressFilterEventsRef.current = false;
    });
  }, [
    activeTab,
    viewMode,
    partnersForMyView,
    officeLocationsForMyView,
    gridKey,
    userId,
    syncFilterIndicatorState,
  ]);

  const handleClearFiltersClick = () => {
    // In MyView, confirm before clearing (overwrites saved preferences)
    if (viewMode === "myView") {
      setShowClearConfirm(true);
    } else {
      clearAllFilters();
    }
  };

  const handleConfirmClear = () => {
    setShowClearConfirm(false);
    clearAllFilters();
  };

  const handleCancelClear = () => {
    setShowClearConfirm(false);
  };

  // ==================== Grid Refresh ====================
  /**
   * Forces a complete grid refresh:
   * 1. Clears first-page cache
   * 2. Triggers CygnusGrid to refetch data
   */
  const handleRefresh = () => {
    firstPageCacheRef.current = {};

    if (activeTab === "prospect") {
      prospectGridRef.current?.refreshGrid();
    } else if (activeTab === "clients") {
      clientGridRef.current?.refreshGrid();
    }

    setTimeout(() => {
      syncFilterIndicatorState(
        undefined,
        String(activeTabRef.current) as "prospect" | "clients"
      );
    }, 150);
  };

  // ==================== Helper Functions ====================
  const deselectRows = () => {
    prospectGridRef.current?.deselectAllRows();
    clientGridRef.current?.deselectAllRows();
  };

  const goToPill = (pill: string) => {
    transporter(pill);
  };

  const handleSelect = (key: EventKey | null) => {
    if (!key) return;

    kpiPayloadHandledRef.current = true;
    clearKpiPayloadFromUrl();

    setActiveTab(key);
  };

  const OneWordPerLine = ({ label }: { label: string }) => (
    <>
      {label
        .trim()
        .split(/\s+/)
        .map((w, i) => (
          <React.Fragment key={i}>
            {i > 0 && <br />}
            {w}
          </React.Fragment>
        ))}
    </>
  );

  // Set Active tab as prospect when cominng to clients page from clients dashboard
  useEffect(() => {
    localStorage.removeItem("clients_dashboard_target_tab");
  }, []);

  // ==================== Filter Value Providers ====================
  /**
   * Creates label maps and value providers for AG Grid filters
   * These handle the display and filtering of dropdown options
   */
  const officeLabelByVal = createLabelMap(
    officeLocationOptions,
    (o) => o.value,
    (o) => o.label
  );
  const partnerLabelByVal = createLabelMap(
    primaryPartnerOptions,
    (o) => o.value,
    (o) => o.label
  );
  const losLabelByVal = createLabelMap(
    purposeOptions,
    (o) => String(o.value ?? o.code),
    (o) => o.label
  );

  const getOfficeLocationValues = createPartnerProvider(officeLocationOptions);
  const getPartnerValues = createPartnerProvider(primaryPartnerOptions);
  const getLosValues = createPurposeProvider(purposeOptions);
  const getUserValues = createUsersProvider(users);
  const getClientTypeValues = createClientTypesProvider(clientTypes);
  const getStatusValues = createStatusProvider(clientStatus);

  /**
   * Selected-first value providers: Show currently selected filter values at the top
   * Improves UX by making it easy to see what's currently filtered
   */
  const officeLocationValuesSelectedFirst = selectedFirstValuesProvider(
    "officeLocation",
    getOfficeLocationValues
  );
  const partnerValuesSelectedFirst = selectedFirstValuesProvider(
    "primaryPartner",
    getPartnerValues
  );
  const originatingPartnerValuesSelectedFirst = selectedFirstValuesProvider(
    "originatingPartner",
    getPartnerValues
  );
  const clientTypeValuesSelectedFirst = selectedFirstValuesProvider(
    "clientTypeName",
    getClientTypeValues
  );
  const losValuesSelectedFirst = selectedFirstValuesProvider(
    "lineOfService",
    getLosValues
  );
  const statusValuesSelectedFirst = selectedFirstValuesProvider(
    "approvalStatus",
    getStatusValues
  );
  const createdByValuesSelectedFirst = selectedFirstValuesProvider(
    "createdBy",
    getUserValues
  );

  // ==================== Column Definitions ====================
  /**
   * Builds column definitions when global options are loaded
   */
  useEffect(() => {
    //if (!filtersReady) return;
    
    const textParams = { debounceMs: 300, caseSensitive: false };

    // Prospects grid columns
    const cols: ColDef[] = [
      {
        field: "__select",
        colId: "select",
        headerName: "",
        checkboxSelection: true,
        headerCheckboxSelection: true,
        suppressHeaderMenuButton: true,
        sortable: false,
        width: 50,
        maxWidth: 50,
        filter: false,
        headerClass: "ag-center-select-header",
        pinned: "left",
      },
      {
        field: "ClientId",
        colId: "clientId",
        headerName: GridColumnTitleRenderer("Client Id"),
        sortable: false,
        valueFormatter: (p) =>
          p.value == null || p.value === "" ? "" : p.value,
        minWidth: 140,
        filter: "agTextColumnFilter",
        filterParams: textParams,
      },
      {
        field: "Client Name",
        colId: "clientName",
        headerName: GridColumnTitleRenderer("Client Name"),
        sortable: false,
        cellRenderer: EditFormButtonRenderer,
        cellRendererParams: (params: any) => ({
          formType: "Client Onboarding",
          rowData: params.data,
          setClientRowData: setClientRowData,
          setFormPurpose: setFormPurpose,
          setClientMasterId: setClientMasterId,
          setClientEditorShow: setProspectEditorShow,
        }),
        minWidth: 180,
        filter: "agTextColumnFilter",
        filterParams: textParams,
      },
      {
        field: "ClientTypeName",
        colId: "clientTypeName",
        headerName: GridColumnTitleRenderer("Client Type"),
        sortable: false,
        filter: "agSetColumnFilter",
        filterParams: {
          values: clientTypeValuesSelectedFirst,
          refreshValuesOnOpen: true,
        },
        minWidth: 140,
      },
      {
        field: "OfficeLocation",
        colId: "officeLocation",
        headerName: "Office Location",
        sortable: false,
        minWidth: 130,
        filter: "agSetColumnFilter",
        filterParams: {
          values: officeLocationValuesSelectedFirst,
          refreshValuesOnOpen: true,
          valueFormatter: (p: any) =>
            officeLabelByVal.get(String(p.value)) ?? "",
        },
        valueFormatter: (p) =>
          officeLabelByVal.get(String(p.value)) ?? p.value ?? "",
      },
      {
        field: "BusinessUnit",
        colId: "lineOfService",
        headerName: GridColumnTitleRenderer("Line of Service"),
        sortable: false,
        filter: "agSetColumnFilter",
        filterParams: {
          values: losValuesSelectedFirst,
          refreshValuesOnOpen: true,
          valueFormatter: (p: any) => losLabelByVal.get(String(p.value)) ?? "",
        },
        valueFormatter: (p) =>
          losLabelByVal.get(String(p.value)) ?? p.value ?? "",
        minWidth: 120,
      },
      {
        field: "PrimaryPartner",
        colId: "primaryPartner",
        headerName: GridColumnTitleRenderer("Primary Partner"),
        sortable: false,
        filter: "agSetColumnFilter",
        filterParams: {
          values: partnerValuesSelectedFirst,
          refreshValuesOnOpen: true,
          valueFormatter: (p: any) =>
            partnerLabelByVal.get(String(p.value)) ?? "",
        },
        valueFormatter: (p) =>
          partnerLabelByVal.get(String(p.value)) ?? p.value ?? "",
        minWidth: 180,
      },
      {
        field: "OriginatingPartner",
        colId: "originatingPartner",
        headerName: GridColumnTitleRenderer("Originating Partner"),
        sortable: false,
        filter: "agSetColumnFilter",
        filterParams: {
          values: originatingPartnerValuesSelectedFirst,
          refreshValuesOnOpen: true,
          valueFormatter: (p: any) =>
            partnerLabelByVal.get(String(p.value)) ?? "",
        },
        valueFormatter: (p) =>
          partnerLabelByVal.get(String(p.value)) ?? p.value ?? "",
        minWidth: 180,
      },
      {
        field: "Approval Status",
        colId: "approvalStatus",
        headerName: GridColumnTitleRenderer("Status"),
        sortable: false,
        filter: "agSetColumnFilter",
        filterParams: {
          values: statusValuesSelectedFirst,
          refreshValuesOnOpen: true,
        },
        cellRenderer: ApprovalStatusRenderer,
        cellRendererParams: (params: any) => ({ rowData: params.data }),
        valueFormatter: (p) =>
          p.value == null || p.value === "" ? "" : p.value,
        minWidth: 160,
      },
      {
        headerName: "Created By",
        field: "CreatedBy",
        colId: "createdBy",
        filter: "agSetColumnFilter",
        filterParams: {
          values: createdByValuesSelectedFirst,
          refreshValuesOnOpen: true,
        },
        minWidth: 180,
      },
      {
        field: "Created Date",
        colId: "createdDate",
        headerName: GridColumnTitleRenderer("Created Date"),
        sortable: false,
        filter: "agDateColumnFilter",
        floatingFilter: true,
        floatingFilterComponent: CreatedDateFloatingFilter,
        floatingFilterComponentParams: {
          suppressFilterButton: true,
        },
        filterParams: {
          filterOptions: ["inRange", "equals", "greaterThan", "lessThan"],
          defaultOption: "inRange",
          suppressAndOrCondition: true,
          maxNumConditions: 1,
          buttons: ["reset", "apply"],
          closeOnApply: true,
          browserDatePicker: true,
        },
        cellRenderer: TimeRenderer,
        cellRendererParams: (p: any) => ({ value: p.value, showTime: true }),
        minWidth: 190,
      },
      {
        field: "Last Updated Date",
        colId: "lastUpdatedDate",
        headerName: GridColumnTitleRenderer("Updated Date"),
        sortable: false,
        filter: false,
        filterParams: dateFilterParams,
        cellRenderer: TimeRenderer,
        cellRendererParams: (p: any) => ({ value: p.value, showTime: true }),
        minWidth: 160,
      },
      {
        field: "nextAction",
        colId: "nextAction",
        headerName: GridColumnTitleRenderer("Next action"),
        sortable: false,
        filter: false,
        minWidth: 180,
        pinned: "right",
        cellRenderer: NextActionRenderer,
        cellRendererParams: (params: any) => ({
          data: params.data,
          onSendClientApproval: (row: any) => {
            handleSendForApprovalWithValidation([row]);
          },
          onOpenELWizard: (row: any) => {
            setClientMasterId(row?.ClientMasterId);
            setClientName(row?.["Client Name"]);
            setAddNewWizardShow(true);
          },
          goToEL: async (row: any) => {
            const clickedClientId = row?.ClientId;
            const userId = localStorage.getItem("userId") || "";

            if (!clickedClientId || !userId) {
              goToPill("client_details");
              return;
            }

            const payloadToSave = {
              gridKey: "EngagementLetterDetailsPage.grid",
              savedAt: new Date().toISOString(),
              viewMode: "myView",
              gridModel: {
                approvalStatus: null,
                signatureStatus: null,
                primaryPartner: null,
                officeLocation: null,
                businessUnit: null,
                fiscalYear: null,
                clientId: {
                  filterType: "text",
                  type: "equals",
                  filter: clickedClientId,
                },
              },
              serverFilter: {
                ClientId: [clickedClientId],
              },
            };

            try {
              await saveGridFilters({
                gridKey: "EngagementLetterDetailsPage.grid",
                userId: String(userId),
                savedAt: payloadToSave.savedAt,
                viewMode: "myView",
                gridModel: payloadToSave.gridModel,
                serverFilter: payloadToSave.serverFilter,
              });

              goToPill("client_details");
            } catch (err) {
              console.error("Failed to save EL client filter:", err);
              goToPill("client_details");
            }
          },
          goToApproval: () => {
            goToPill("client_approval");
          },
        }),
      },
      {
        field: "actions",
        colId: "actions",
        headerName: "Actions",
        pinned: "right",
        sortable: false,
        minWidth: 145,
        maxWidth: 145,
        suppressHeaderMenuButton: true,
        suppressColumnsToolPanel: true,
        cellStyle: { "border-right-color": "#ccc" },
        cellRenderer: ActionsRenderer,
        cellRendererParams: (params: any) => ({
          data: params.data,
          setUploaderShow,
          setAddNewWizardShow,
          setSendForApprovalBulkApiProgress: () => { },
          setSelectedRows: setSelectedRowsProspects,
          deselectRows,
          setApprovalSettingModal,
          setClientMasterId,
          setClientName,
          setIsClientPublic,
          setClientTypeCode,
          setClientId: setClientId,
          setAuditTrailModalShow: setAuditTrailModalShow,
          setApprovalPdfViewerShow: setApprovalPdfViewerShow,
          setClientDetailsPdfUrl: setClientDetailsPdfUrl,
          sendForApproval: handleSendForApprovalWithValidation,
        }),
        filter: false,
      },
    ];
    setProspectColumnDefs(cols);

    // Clients in Practice grid columns
    const cch_cols: ColDef[] = [
      {
        field: "ClientId",
        colId: "clientId",
        headerName: GridColumnTitleRenderer("Client Id"),
        sortable: false,
        valueFormatter: (p) =>
          p.value == null || p.value === "" ? "" : p.value,
        minWidth: 140,
        filter: "agTextColumnFilter",
        filterParams: textParams,
      },
      {
        field: "Client Name",
        colId: "clientName",
        headerName: GridColumnTitleRenderer("Client Name"),
        sortable: false,
        cellRenderer: EditFormButtonRenderer,
        cellRendererParams: (params: any) => ({
          formType: "Client Onboarding",
          rowData: params.data,
          setClientRowData: setClientRowData,
          setFormPurpose: setFormPurpose,
          setClientMasterId: setClientMasterId,
          setClientEditorShow: setClientEditorShow,
        }),
        minWidth: 180,
        filter: "agTextColumnFilter",
        filterParams: textParams,
      },
      {
        field: "ClientTypeName",
        colId: "clientTypeName",
        headerName: GridColumnTitleRenderer("Client Type"),
        sortable: false,
        filter: "agSetColumnFilter",
        filterParams: {
          values: clientTypeValuesSelectedFirst,
          refreshValuesOnOpen: true,
        },
        minWidth: 140,
      },
      {
        field: "OfficeLocation",
        colId: "officeLocation",
        headerName: "Office Location",
        sortable: false,
        minWidth: 130,
        filter: "agSetColumnFilter",
        filterParams: {
          values: officeLocationValuesSelectedFirst,
          refreshValuesOnOpen: true,
          valueFormatter: (p: any) =>
            officeLabelByVal.get(String(p.value)) ?? "",
        },
        valueFormatter: (p) =>
          officeLabelByVal.get(String(p.value)) ?? p.value ?? "",
      },
      {
        field: "BusinessUnit",
        colId: "lineOfService",
        headerName: GridColumnTitleRenderer("Line of Service"),
        sortable: false,
        minWidth: 120,
        filter: "agSetColumnFilter",
        filterParams: {
          values: getLosValues,
          refreshValuesOnOpen: true,
          valueFormatter: (p: any) => losLabelByVal.get(String(p.value)) ?? "",
        },
        valueFormatter: (p) =>
          losLabelByVal.get(String(p.value)) ?? p.value ?? "",
      },
      {
        field: "PrimaryPartner",
        colId: "primaryPartner",
        headerName: GridColumnTitleRenderer("Primary Partner"),
        sortable: false,
        filter: "agSetColumnFilter",
        filterParams: {
          values: partnerValuesSelectedFirst,
          refreshValuesOnOpen: true,
          valueFormatter: (p: any) =>
            partnerLabelByVal.get(String(p.value)) ?? "",
        },
        valueFormatter: (p) =>
          partnerLabelByVal.get(String(p.value)) ?? p.value ?? "",
        minWidth: 180,
      },
      {
        field: "OriginatingPartner",
        colId: "originatingPartner",
        headerName: GridColumnTitleRenderer("Originating Partner"),
        sortable: false,
        minWidth: 180,
        filter: "agSetColumnFilter",
        filterParams: {
          values: originatingPartnerValuesSelectedFirst,
          refreshValuesOnOpen: true,
          valueFormatter: (p: any) =>
            partnerLabelByVal.get(String(p.value)) ?? "",
        },
        valueFormatter: (p) =>
          partnerLabelByVal.get(String(p.value)) ?? p.value ?? "",
      },
      {
        field: "Approval Status",
        colId: "approvalStatus",
        headerName: GridColumnTitleRenderer("Status"),
        sortable: false,
        minWidth: 160,
        filter: "agSetColumnFilter",
        filterParams: {
          values: statusValuesSelectedFirst,
          refreshValuesOnOpen: true,
        },
        cellRenderer: ApprovalStatusRenderer,
        cellRendererParams: (params: any) => ({ rowData: params.data }),
        valueFormatter: (p) =>
          p.value == null || p.value === "" ? "" : p.value,
      },
      {
        headerName: "Created By",
        field: "CreatedBy",
        colId: "createdBy",
        minWidth: 180,
        filter: "agSetColumnFilter",
        filterParams: {
          values: getUserValues,
          refreshValuesOnOpen: true,
        },
      },
      {
        field: "Created Date",
        colId: "createdDate",
        headerName: GridColumnTitleRenderer("Created Date"),
        sortable: false,
        filter: "agDateColumnFilter",
        floatingFilter: true,
        floatingFilterComponent: CreatedDateFloatingFilter,
        floatingFilterComponentParams: {
          suppressFilterButton: true,
        },
        filterParams: {
          filterOptions: ["inRange", "equals", "greaterThan", "lessThan"],
          defaultOption: "inRange",
          suppressAndOrCondition: true,
          maxNumConditions: 1,
          buttons: ["reset", "apply"],
          closeOnApply: true,
          browserDatePicker: true,
        },
        cellRenderer: TimeRenderer,
        cellRendererParams: (p: any) => ({ value: p.value, showTime: true }),
        minWidth: 190,
      },
      {
        field: "Last Updated Date",
        colId: "lastUpdatedDate",
        headerName: GridColumnTitleRenderer("Updated Date"),
        sortable: false,
        filter: false,
        filterParams: dateFilterParams,
        cellRenderer: TimeRenderer,
        cellRendererParams: (p: any) => ({ value: p.value, showTime: true }),
        minWidth: 160,
      },
      {
        field: "actions",
        colId: "actions",
        headerName: "Actions",
        pinned: "right",
        minWidth: 90,
        maxWidth: 90,
        sortable: false,
        suppressHeaderMenuButton: true,
        cellRenderer: ClientActionsRenderer,
        cellStyle: { "border-right-color": "#ccc" },
        cellRendererParams: (params: any) => ({
          data: params.data,
          setAddNewWizardShow: setAddNewWizardShow,
          setClientMasterId: setClientMasterId,
          setClientName: setClientName,
          setClientId: setClientId,
          setAuditTrailModalShow: setAuditTrailModalShow,
          setApprovalPdfViewerShow: setApprovalPdfViewerShow,
          setClientDetailsPdfUrl: setClientDetailsPdfUrl
        }),
        filter: false,
      },
    ];
    setClientColumnDefs(cch_cols);
  }, [filtersReady, clientGroups, clientStatus, purposeOptions, officeLocationOptions, primaryPartnerOptions, users, clientTypes]);

  const handleActionAlertContinue = async () => {
    const response = await createInCCH(
      selectedRowsProspects,
      setCreateInCCHApiProgress
    )

    if (response) {
      handleCancel();
      handleRefresh();
    }
  };

  const handleCancel = () => {
    setShowActionAlert(false);
  };

  const updateCreateInCCHDisabledReason = (selectedRows: any[]) => {
    setAllApprove(
      selectedRows?.every(
        (row: any) => (row.ApprovalStatus || "").toLowerCase() === "approved"
      )
    );

    let reason: string | null = null;

    if (!selectedRows || selectedRows.length === 0) {
      reason = "Please select one or more clients before creating in CCH.";
    } else if (
      selectedRows.some(
        (row: any) => (row.ApprovalStatus || "").toLowerCase() === "rejected"
      )
    ) {
      reason = "Rejected clients cannot be created in CCH.";
    }

    setCreateInCCHDisabledReason(reason);
  };
   
const updateRevertApprovalDisabledReason = (selectedRows: any[]) => {
  if (!selectedRows?.length) {
    setRevertApprovalDisabledReason(
      "Please select one or more clients before reverting approval status."
    );
    return;
  }

  const hasNonApprovedClient = selectedRows.some(
    (row: any) =>
      !["approved", "auto approved", "partially approved"].includes(
        (row.ApprovalStatus || "").trim().toLowerCase()
      )
  );

  setRevertApprovalDisabledReason(
    hasNonApprovedClient
      ? "Only approved clients can be reverted to pending approval."
      : null
  );
};
  const updateSendForApprovalDisabledReason = (selectedRows: any[]) => {
    let reason: string | null = null;

    if (!selectedRows || selectedRows.length === 0) {
      reason = "Please select one or more clients before sending for approval.";
    } else if (
      selectedRows.some(
        (row: any) => (row.ApprovalStatus || "").toLowerCase().includes("approved")
      )
    ) {
      reason = "One or more selected clients are already approved.";
    }

    setSendForApprovalDisabledReason(reason);
  };

  const handleApprovalCheck = (actionCallback: () => void) => {
    if (allApprove) {
      actionCallback();
    } else {
      setActionAlertTitle("Auto-Approve Clients");
      setActionAlertMessage(
        "If you continue, any unapproved clients will be automatically approved and submitted for CCH creation."
      );
      setShowActionAlert(true);
    }
  };

  const handleCreateInCCHAction = () => {
    handleApprovalCheck(async () => {
      handleActionAlertContinue();
    });
  }
  const handleOpenRevertApprovalConfirm = () => {
    const ids = (selectedRowsProspects || [])
      .map((row: any) => Number(row.ClientMasterId))
      .filter((id: number) => !Number.isNaN(id));

    if (!ids.length) return;

    setSelectedRevertReason("");
    setRevertApprovalCount(ids.length);
    setRevertApprovalMessage("");
    setShowRevertApprovalConfirm(true);
    setRevertComment("");
    setRevertApprovalClientIds(selectedRowsProspects.map((row:any):string => row.ClientId));
  };
  const resetRevertApprovalConfirm = () => {
    setShowRevertApprovalConfirm(false);
    setRevertApprovalMessage("");
    setRevertApprovalClientIds([]);
    setSelectedRevertReason("");
    setRevertComment("");
    setRevertApprovalCount(0);
  };

  const handleRevertApproval = async (rows: any[]) => {
    if (!rows?.length){
         resetRevertApprovalConfirm();
      return;
    }
    try{
      const filteredData= rows.map(item => ({
      ClientMasterId: item.ClientMasterId
    }));
    const revertReason = revertComment.trim()
      ? `${selectedRevertReason}: ${revertComment.trim()}`
      : selectedRevertReason;
    const response = await revertApprovalStatus(
      revertReason,
      filteredData,
      setRevertApprovalApiProgress,
      setSelectedRowsProspects,
      deselectRows
    );
    if (response) {
      handleRefresh();
      resetRevertApprovalConfirm();
    }
  } finally {
    setRevertApprovalApiProgress(false);
    }
  }

  const handleSendForApprovalWithValidation = async (rows: any[]) => {
    console.log("[handleSendForApprovalWithValidation] Starting with rows:", rows.length);

    const response = await sendForApproval(
      rows,
      setSendForApprovalBulkApiProgress,
      setSelectedRowsProspects,
      deselectRows
    );

    console.log("[handleSendForApprovalWithValidation] Response:", response);

    if (!response) return;

    const { data } = response;
    if (!data) return;

    const { invalidClients = [], totalProcessed = 0, totalInvalid = 0 } = data;

    if (invalidClients && invalidClients.length > 0) {
      // Separate by reason type
      const missingDocsClients = invalidClients.filter(
        (c: any) => c.reason === "MISSING_DOCUMENTS" || (c.missingDocuments?.length > 0 && c.reason !== "NO_APPROVER")
      );
      
      const noApproverClients = invalidClients.filter(
        (c: any) => c.reason === "NO_APPROVER"
      );

      const missingDataClients = invalidClients.filter(
        (c: any) => c.reason === "MISSING_REQUIRED_DATA"
      );

      // Build the warning message based on what failed
      let title = "";
      let message = "";
      let listItems: string[] = [];

      if (noApproverClients.length > 0 && missingDocsClients.length === 0) {
        // Only approver issues
        title = "Approver Not Assigned";
        if (rows.length === 1) {
          message = `"${noApproverClients[0].ClientName}" cannot be sent for approval. Please assign an approver first.`;
        } else {
          message = totalProcessed > 0
            ? `${totalProcessed} client(s) were sent for approval. The following client(s) need an approver assigned:`
            : `The following client(s) need an approver assigned before sending for approval:`;
          listItems = noApproverClients.map((c: any) => c.ClientName);
        }
      } else if (missingDocsClients.length > 0 && noApproverClients.length === 0) {
        // Only document issues
        title = "Missing Required Documents";
        if (rows.length === 1) {
          message = `"${missingDocsClients[0].ClientName}" cannot be sent for approval. Please upload the following required documents:`;
          listItems = (missingDocsClients[0].missingDocuments || []).sort((a: string, b: string) =>
            a.localeCompare(b)
          );
        } else {
          message = totalProcessed > 0
            ? `${totalProcessed} client(s) were sent for approval. The following client(s) have missing required documents:`
            : `The following client(s) have missing required documents and could not be sent for approval:`;
          listItems = missingDocsClients.map(
            (c: any) => `${c.ClientName} - Missing: ${(c.missingDocuments || []).join(", ")}`
          );
        }
      } else if (missingDataClients.length > 0 && noApproverClients.length === 0 && missingDocsClients.length === 0) {
        title = "Incomplete Client Data";

        const processedText = totalProcessed === 1 ? "client was" : "clients were";
        const affectedText = missingDataClients.length === 1 ? "client has" : "clients have";

        if (rows.length === 1) {
          message = `"${missingDataClients[0].ClientName}" cannot be sent for approval. Client data is incomplete. Complete required fields.`;
        } else {
          message = totalProcessed > 0
            ? `${totalProcessed} ${processedText} sent for approval. The following ${affectedText} incomplete data:`
            : `The following ${affectedText} incomplete data and could not be sent for approval:`;

          listItems = missingDataClients.map((c: any) => c.ClientName);
        }
      } else {
        // Mixed issues (both approvers and documents)
        title = "Cannot Send for Approval";
        message = totalProcessed > 0
          ? `${totalProcessed} client(s) were sent for approval. The following client(s) have issues:`
          : `The following client(s) could not be sent for approval:`;

        // Combine both lists
        const approverItems = noApproverClients.map(
          (c: any) => `${c.ClientName} - No approver assigned`
        );
        const docItems = missingDocsClients.map((c: any) => {
          const sortedDocs = (c.missingDocuments || []).sort((a: string, b: string) =>
            a.localeCompare(b)
          );
          return `${c.ClientName} - Missing: ${sortedDocs.join(", ")}`;
        });
        listItems = [...approverItems, ...docItems];
      }

      setDocumentWarningTitle(title);
      setDocumentWarningMessage(message);
      setDocumentWarningList(listItems);
      setShowDocumentWarning(true);
    }

    if (totalProcessed > 0) {
      handleRefresh();
    }
  };

  useEffect(() => {
    setSelectedRowsClients([]);
    setSelectedRowsProspects([]);
  }, [activeTab])

  /**
   * Update button disabled states when selection changes
   */
  useEffect(() => {
    updateCreateInCCHDisabledReason(selectedRowsProspects);
    updateSendForApprovalDisabledReason(selectedRowsProspects);
    updateRevertApprovalDisabledReason(selectedRowsProspects);
  }, [refreshApiProgress, activeTab])

  const renderTabTitle = (label: string, tabKey: EventKey) => (
    <span className={styles.tabTitleWithFilter}>
      <span>{label}</span>

      {activeTab === tabKey &&
        savedLoadedByTab[String(tabKey)] &&
        filterIndicatorReady &&
        hasActiveFilters &&
        activeFilterCount > 0 && (
          <span className={styles.tabFilterBadge}>
            <FunnelFill className={styles.tabFilterIcon} />
            <span className={styles.tabFilterCount}>{activeFilterCount}</span>
          </span>
        )}
    </span>
  );

  // ==================== Render ====================
  return (
    <>
      {/* Modals */}

      {/* Document Validation Warning Modal */}
      <AlertModal
        show={showDocumentWarning}
        onHide={() => setShowDocumentWarning(false)}
        title={documentWarningTitle}
        message={documentWarningMessage}
        type="warning"
        size="lg"
        listItems={documentWarningList}
        footerButtons={[
          {
            label: "Close",
            onClick: () => setShowDocumentWarning(false),
          },
        ]}
      />

      {auditTrailModalShow && (
        <AuditTrailModal
          clientMasterId={clientMasterId}
          clientId={clientId}
          clientName={clientName}
          show={auditTrailModalShow}
          onHide={() => setAuditTrailModalShow(false)}
          title="Audit Trail"
        />
      )}

      {approvalPdfViewerShow && (
        <ApprovalPdfViewerModal
          show={approvalPdfViewerShow}
          onHide={() => setApprovalPdfViewerShow(false)}
          clientMasterId={clientMasterId}
          clientName={clientName}
          pdfUrl={clientDetailsPdfUrl}
        />
      )}

      {prospectEditorShow && (
        <ClientEditor
          show={prospectEditorShow}
          onHide={() => setProspectEditorShow(false)}
          refresh={() => handleRefresh()}
          clientMasterId={clientMasterId}
          formPurpose={formPurpose}
          clientData={clientRowData}
          disableEdit={disableApprovedProspect}
          allowApprovedEdit={isFirmAdmin}
        />
      )}

      {clientEditorShow && (
        <ClientEditor
          show={clientEditorShow}
          onHide={() => setClientEditorShow(false)}
          refresh={() => handleRefresh()}
          clientMasterId={clientMasterId}
          formPurpose={formPurpose}
          clientData={clientRowData}
          disableEdit={true}
        />
      )}

      {clientCreatorShow && (
        <ClientCreator
          show={clientCreatorShow}
          refresh={() => handleRefresh()}
          onHide={() => setClientCreatorShow(false)}
          purpose={clientPurpose}
        />
      )}

      {addNewWizardShow && (
        <CreateEngagementLetterWizard
          show={addNewWizardShow}
          onHide={() => setAddNewWizardShow(false)}
          clientMasterId={clientMasterId}
          engagementlettertemplates={(elt) => {
            // PRELOADED TEMPLATES NOT NEEDED HERE
          }}
          setapiflag={(flag) => {
            // NOT NEEDED HERE
          }}
        />
      )}

      {approvalSettingModal && (
        <ApprovalSettingsModal
          title={"Approval Setting"}
          clientMasterId={clientMasterId}
          clientTypeCode={clientTypeCode}
          show={approvalSettingModal}
          disableCheck={true}
          onHide={() => setApprovalSettingModal(false)}
        />
      )}

      {uploaderShow && (
        <ClientFilesUploader
          show={uploaderShow}
          onHide={() => setUploaderShow(false)}
          clientName={clientName}
          clientMasterId={clientMasterId}
          isPublic={isClientPublic}
          refresh={() => handleRefresh()}
        />
      )}

      {showGroupModal && (
        <ClientGroupManagerModal
          show={showGroupModal}
          onHide={() => setShowGroupModal(false)}
          groupData={clientGroups}
          refreshGroups={() => {
            fetchClientGroups().then((res) => setClientGroups(res));
          }}
        />
      )}

      {downloadExcelDataSpreadSheet && (
        <ExportClientsToExcelModal
          show={downloadExcelDataSpreadSheet}
          onHide={() => setDownloadExcelDataSpreadSheetShow(false)}
          engagementlettertemplates={engagementLetterTemplateOptions}
          selectedclients={selectedRowsClients}
        />
      )}

      {uploadSpreadsheetModal && (
        <UploadSpreadsheetModal
          show={uploadSpreadsheetModal}
          onHide={() => setUploadSpreadsheetModalShow(false)}
          refresh={handleRefresh}
          options={purposeOptions as BackendOption[]}
        />
      )}

      {showClearConfirm && (
        <AlertModal
          show={showClearConfirm}
          onHide={handleCancelClear}
          title="Unsaved Changes"
          message="Reset Filters will remove all your current filters and overwrite your saved My View configuration with the default settings. You will need to reapply any custom filters again."
          type="warning"
          listItems={[]}
          footerButtons={[
            { label: "Cancel", onClick: handleCancelClear },
            { label: "Continue", onClick: handleConfirmClear },
          ]}
        />
      )}

      <AlertModal
        show={showActionAlert}
        onHide={handleCancel}
        title={actionAlertTitle}
        size="lg"
        message={actionAlertMessage}
        type="warning"
        listItems={[]}
        footerButtons={[
          { label: "Cancel", onClick: handleCancel, type: "quinary" },
          {
            label: "Continue",
            onClick: handleActionAlertContinue,
            progress: createInCCHApiProgress,
            type: "tertiary",
          },
        ]}
      />

      {showDeleteConfirm && (
        <AlertModal
          show={showDeleteConfirm}
          onHide={() => {
            setShowDeleteConfirm(false);
            setSelectedDeleteReason("");
            setDeleteComment("");
            setDeleteValidationDone(false);
            setDeleteValidationLoading(false);
            setDeleteWarningCount(0);
            setDeleteMessage("");
            setDeleteWarningSignedClientIds([]);
          }}
          title="Delete Prospects"
          type="warning"
          message={
            deleteValidationLoading
              ? "Checking selected prospects..."
              : deleteWarningCount > 0
                ? (
                  deleteMessage ||
                  "You are about to delete " +
                  deleteCount +
                  " prospect(s).\n" +
                  "Warning: " +
                  deleteWarningCount +
                  " selected prospect(s) have linked engagement letters that may already be sent out for signature.\n" +
                  "Client ID(s): " +
                  deleteWarningSignedClientIds.join(", ") +
                  "\n\n" +
                  "This action will remove the prospects and their linked engagement letters."
                )
                : deleteValidationDone
                  ? (
                    deleteMessage ||
                    "You are about to delete " +
                    deleteCount +
                    " prospect(s).\n\n" +
                    "This action will remove them from the grid."
                  )
                  : ""
          }
          listItems={[]}
          bodyContent={
            !deleteValidationLoading ? (
              <div>
                <div style={{ marginBottom: "1rem" }}>
                  <label style={{ display: "block", marginBottom: "0.35rem", fontWeight: 500 }}>
                    Delete Reason
                  </label>
                  <select
                    value={selectedDeleteReason}
                    onChange={(e) => setSelectedDeleteReason(e.target.value)}
                    style={{ width: "100%", padding: "8px" }}
                  >
                    <option value="">Select reason</option>
                    {prospectDeleteReasonOptions.map((opt: any) => (
                      <option key={opt.id || opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label style={{ display: "block", marginBottom: "0.35rem", fontWeight: 500 }}>
                    Comment
                  </label>
                  <textarea
                    value={deleteComment}
                    onChange={(e) => setDeleteComment(e.target.value)}
                    maxLength={100}
                    rows={3}
                    style={{ width: "100%", padding: "8px", resize: "vertical" }}
                    placeholder="Add comment if needed"
                  />
                </div>
              </div>
            ) : null
          }
          footerButtons={[
            {
              label: "Cancel",
              onClick: () => {
                setShowDeleteConfirm(false);
                setSelectedDeleteReason("");
                setDeleteComment("");
                setDeleteValidationDone(false);
                setDeleteValidationLoading(false);
                setDeleteWarningCount(0);
                setDeleteMessage("");
                setDeleteWarningSignedClientIds([]);
              },
            },
            ...(deleteValidationLoading
              ? []
              : [
                {
                  label: "Delete",
                  onClick: handleConfirmDeleteProspects,
                  progress: deleteInProgress,
                  disabled: !selectedDeleteReason,
                  type: "tertiary" as const,
                },
              ]),
          ]}
        />
      )}
      {showRevertApprovalConfirm && (
        <AlertModal
        show={showRevertApprovalConfirm}
        onHide={resetRevertApprovalConfirm}
        title="Revert Approval"
        type="warning"
        message={revertApprovalMessage ||
          `You are about to revert approval status for ${revertApprovalCount} client(s) to Pending Approval.`
        }
        listItems={
          revertApprovalClientIds?.length
          ? revertApprovalClientIds.map(
          (id:string) => `Client ID: ${id}`
          ): []
        }
        bodyContent={
              <div>
                <div style={{ marginBottom: "1rem" }}>
                  <label style={{ display: "block", marginBottom: "0.35rem", fontWeight: 500 }}>
                    Revert Reason
                  </label>
                  <select
                    value={selectedRevertReason}
                    onChange={(e) => setSelectedRevertReason(e.target.value)}
                    style={{ width: "100%", padding: "8px" }}
                  >
                    <option value="">Select reason</option>
                    {prospectRevertReasonOptions.map((opt: any) => (
                      <option key={opt.id || opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
              <div>
                <label
                    style={{
                    display: "block",
                    marginBottom: "0.55rem",
                    fontWeight: 500,
                  }}>
                  Comment
                </label>
                <textarea
                  value={revertComment}
                  onChange={(e) => setRevertComment(e.target.value)}
                  maxLength={100}
                  rows={3}
                  style={{
                    width: "100%",
                    padding: "12px",
                    resize: "vertical",
                  }}
                  placeholder="Add comment if needed"
                />
              </div> 
              </div>
          }
        footerButtons={[
        {
          label: "Cancel",
          onClick: ()=>resetRevertApprovalConfirm()
        },
        {
          label: "Revert Approval",
          onClick: () => {
            handleRevertApproval(selectedRowsProspects);
            },
            progress: revertApprovalApiProgress,
            disabled: !selectedRevertReason,
            type: "tertiary" as const,
        },
      ]}
    />
  )}

      {/* Header */}
      <Row className={styles.clientGridHeader}>
        <div className={styles.titleRowLeft}>
          <span className={styles.clientTitle}>Clients</span>

          <ViewToggle
            mode={viewMode}
            onChange={(m: ViewMode) => setViewMode(m)}
            size="lg"
          />

          <button
            type="button"
            className={styles.clearButton}
            onClick={handleClearFiltersClick}
          >
            Reset filters
          </button>
        </div>
        <div className={styles.clientsButtonsContainer}>

          {activeTab === "prospect" && canDeleteProspect && (
            <>
              <OverlayTrigger
                placement="bottom"
                overlay={
                  deleteTooltipMessage ? (
                    <Tooltip id="delete-prospect-tooltip" style={{ fontSize: "10px" }}>
                      {deleteTooltipMessage}
                    </Tooltip>
                  ) : (
                    <></>
                  )
                }
              >
                <div className="d-inline-block">
                  <CustomButton
                    disabled={deleteDisabled}
                    onclick={handleOpenDeleteConfirm}
                    content={"Delete"}
                  />
                </div>
              </OverlayTrigger>
              <div style={{ marginRight: 20 }} />
            </>
          )}
          {activeTab === "prospect" && showBulkCreate && (
            <>
              <CustomButton
                onclick={() => setUploadSpreadsheetModalShow(true)}
                content={"Bulk Create"}
              />
              <div style={{ marginRight: 20 }} />
            </>
          )}
          {activeTab === "prospect" &&  isFirmAdmin &&
          (
            <>
              <OverlayTrigger
                placement="bottom"
                overlay={
                  revertApprovalDisabledReason ? (
                    <Tooltip id="button-tooltip">
                      {revertApprovalDisabledReason}
                    </Tooltip>
                  ) : (
                    <></>
                  )
                }
              >
                <div className="d-inline-block" style={{ marginRight: 20 }} >
                  <CustomButton
                    onclick={handleOpenRevertApprovalConfirm}
                    disabled={!!revertApprovalDisabledReason}
                    progress={revertApprovalApiProgress}
                    content={"Revert Approval"}
                  />
                </div>
                 </OverlayTrigger>
          </>
          )}

          {activeTab === "prospect" && (
            <>
              <OverlayTrigger
                placement="bottom"
                overlay={
                  sendForApprovalDisabledReason ? (
                    <Tooltip id="button-tooltip">
                      {sendForApprovalDisabledReason}
                    </Tooltip>
                  ) : (
                    <></>
                  )
                }
              >
                <div className="d-inline-block">
                  <CustomButton
                    disabled={!!sendForApprovalDisabledReason}
                    progress={sendForApprovalBulkApiProgress}
                    onclick={() => handleSendForApprovalWithValidation(selectedRowsProspects)}
                    content={"Send for Approval"}
                  />
                </div>
              </OverlayTrigger>
            </>
          )}

          <div style={{ marginRight: 20 }} />

          {activeTab === "prospect" && showCreateInCCH && (
            <>
              <OverlayTrigger
                placement="bottom"
                overlay={
                  createInCCHDisabledReason ? (
                    <Tooltip id="button-tooltip">
                      {createInCCHDisabledReason}
                    </Tooltip>
                  ) : (
                    <></>
                  )
                }
              >
                <div className="d-inline-block">
                  <CustomButton
                    disabled={!!createInCCHDisabledReason}
                    progress={createInCCHApiProgress}
                    onclick={handleCreateInCCHAction}
                    content={"Create in CCH"}
                  />
                </div>
              </OverlayTrigger>
              <div style={{ marginRight: 20 }} />
            </>
          )}


          <Dropdown>
            <Dropdown.Toggle
              className={styles.addClientDropdown}
              id="add-client-dropdown"
            >
              Add New
            </Dropdown.Toggle>

            <Dropdown.Menu className={styles.addClientMenu}>
              {purposeLoading && (
                <Dropdown.Item disabled className={styles.addClientMenuItem}>
                  Loading…
                </Dropdown.Item>
              )}

              {purposeError && (
                <Dropdown.Item disabled className={styles.addClientMenuItem}>
                  {purposeError}
                </Dropdown.Item>
              )}

              {!purposeLoading &&
                !purposeError &&
                purposeOptions.map((opt) => (
                  <Dropdown.Item
                    key={String(opt.code)}
                    className={styles.addClientMenuItem}
                    onClick={() => {
                      setClientPurpose(opt);
                      setClientCreatorShow(true);
                    }}
                  >
                    <OneWordPerLine label={opt.label} />
                  </Dropdown.Item>
                ))}
            </Dropdown.Menu>
          </Dropdown>

          <div style={{ marginRight: 20 }} />

          <RefreshGridButton
            refreshGridApiProgress={refreshApiProgress}
            handleRefresh={handleRefresh}
          />
        </div>
      </Row>

      {/* Grid Tabs */}
      <div className={styles.gridTab}>
        <Tabs
          activeKey={activeTab}
          onSelect={handleSelect}
          defaultActiveKey={"clients"}
        >
          <Tab eventKey="prospect" title={renderTabTitle("Prospects", "prospect")} className="tabs">
            <Row className={styles.clientGridContainer}>
              <CygnusGrid
                key={`prospect-grid-${filtersReady ? "ready" : "loading"}`}
                ref={prospectGridRef}
                columnDefs={prospectColumnDefs}
                gridContext={{
                  gridKey: "ClientsGrid.prospects",
                  userId,
                  viewMode,
                  filterKeyMap: prospectFilterKeyMap,
                }}
                fetchData={wrappedFetchFirmClients}
                searchText={debouncedSearchText}
                dataIdentifier={"ClientMasterId"}
                rowSelection={"multiple"}
                onLoadingChange={setRefreshApiProgress}
                floatingFilters={true}
                onSelectionChange={(rows) => {
                  setSelectedRowsProspects(rows);
                  updateCreateInCCHDisabledReason(rows);
                  updateSendForApprovalDisabledReason(rows);
                  updateRevertApprovalDisabledReason(rows);
                }}
                filterKeyMap={prospectFilterKeyMap}
                onGridReady={(p) => {
                  attachMyViewFilterListener(p.api, "prospect");
                  applyViewModeFilters(p.api, "prospect");
                }}
              />
            </Row>
          </Tab>

          <Tab eventKey="clients" title={renderTabTitle("Clients in Practice", "clients")} className="tabs">
            <Row className={styles.clientGridContainer}>
              <CygnusGrid
                key={`clients-grid-${filtersReady ? "ready" : "loading"}`}
                ref={clientGridRef}
                columnDefs={clientColumnDefs}
                gridContext={{
                  gridKey: "ClientsGrid.clients",
                  userId,
                  viewMode,
                  filterKeyMap: clientFilterKeyMap,
                }}
                fetchData={wrappedFetchFirmClients}
                searchText={debouncedSearchText}
                sourceCode={"CCH_AXCESS"}
                dataIdentifier={"ClientMasterId"}
                rowSelection={"multiple"}
                onLoadingChange={setRefreshApiProgress}
                floatingFilters={true}
                onSelectionChange={(rows) => {
                  setSelectedRowsClients(rows);
                }}
                filterKeyMap={clientFilterKeyMap}
                onGridReady={(p) => {
                  attachMyViewFilterListener(p.api, "clients");
                  applyViewModeFilters(p.api, "clients");
                }}
              />
            </Row>
          </Tab>
        </Tabs>
        
      </div>
    </>
  );
};

export default ClientsGrid;
